import React from 'react';
import SegmentTable from './SegmentTable';
import OptimizationSuggestions from './OptimizationSuggestions';

// Use memo to wrap the component and avoid unnecessary re-renders
const ComparisonResult = React.memo(function ComparisonResult({ 
  pathDifference, 
  autoPathInfo, 
  customPathInfo, 
  expandedDetails, 
  toggleDetails,
  autoSegments,
  customSegments
}) {
  // Move all useMemo calls before conditional statements
  // Use useMemo to cache computed results
  const pointCountDiff = React.useMemo(() => {
    if (autoPathInfo && customPathInfo) {
      return autoPathInfo.pointCount !== customPathInfo.pointCount;
    }
    return false;
  }, [autoPathInfo, customPathInfo]);

  // Use useMemo to cache conclusion text
  const conclusionText = React.useMemo(() => {
    if (!pathDifference) return '';
    
    // Handle equal paths
    if (pathDifference.isEffectivelyEqual || pathDifference.isCustomBetter === null) {
      return 'Both paths have essentially the same distance. Great job!';
    }
    
    return pathDifference.isCustomBetter 
      ? 'Your manual planning path is better than the algorithm-generated path!' 
      : 'The algorithm-generated path is better than your manual planning path.';
  }, [pathDifference]);
  
  // Use useMemo to check if we can make a valid comparison
  const canCompare = React.useMemo(() => {
    return autoPathInfo && 
           customPathInfo && 
           typeof autoPathInfo.distance === 'number' && 
           typeof customPathInfo.distance === 'number';
  }, [autoPathInfo, customPathInfo]);

  // Check which paths are missing or invalid
  const missingPathsMessage = React.useMemo(() => {
    if (!autoPathInfo && !customPathInfo) {
      return "Both auto and custom paths are required for comparison";
    } else if (!autoPathInfo) {
      return "Auto path is missing or invalid";
    } else if (!customPathInfo) {
      return "Custom path is missing or invalid";
    } else if (typeof autoPathInfo.distance !== 'number') {
      return "Auto path distance information is invalid";
    } else if (typeof customPathInfo.distance !== 'number') {
      return "Custom path distance information is invalid";
    } else if (autoPathInfo.distance <= 0) {
      return "Auto path has zero or negative distance";
    } else if (customPathInfo.distance <= 0) {
      return "Custom path has zero or negative distance";
    }
    return null;
  }, [autoPathInfo, customPathInfo]);

  // Early return after all Hooks
  if (!pathDifference) {
    return (
      <div className="comparison-results error">
        <h5>Comparison Not Available</h5>
        <div className="diff-container">
          <p className="error-message">{missingPathsMessage}</p>
          <p className="error-hint">Please make sure both auto and custom paths are properly created in their respective modes.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="comparison-results">
      <h5>Comparison Results</h5>
      <div className="diff-container">
        <p className="diff-value">
          <span className={
            pathDifference.isEffectivelyEqual || pathDifference.isCustomBetter === null 
              ? 'equal' 
              : pathDifference.isCustomBetter ? 'better' : 'worse'
          }>
            {pathDifference.isEffectivelyEqual || pathDifference.isCustomBetter === null 
              ? `Difference: ${pathDifference.absoluteDiff.toFixed(2)} km (${Math.abs(pathDifference.percentDiff).toFixed(2)}% - Negligible)`
              : `${pathDifference.isCustomBetter ? 'Saved ' : 'Added '}${pathDifference.absoluteDiff.toFixed(2)} km (${Math.abs(pathDifference.percentDiff).toFixed(2)}%)`
            }
          </span>
        </p>
        <p className="diff-conclusion">
          {conclusionText}
        </p>
        
        {/* Display hint for different point counts */}
        {pointCountDiff && (
          <p className="point-count-diff">
            <strong>Note:</strong> Auto path has {autoPathInfo.pointCount} points, while custom path has {customPathInfo.pointCount} points. Different point counts may affect distance comparison.
          </p>
        )}
        
        {/* Add detailed path comparison button */}
        <button 
          className="toggle-details-btn"
          onClick={toggleDetails}
        >
          {expandedDetails ? 'Hide Detailed Comparison' : 'Show Detailed Comparison'}
        </button>
        
        {/* Detailed path segment comparison */}
        {expandedDetails && (
          <div className="segments-comparison">
            <h6>Detailed Path Segment Comparison</h6>
            
            {/* Auto path segments table */}
            <SegmentTable 
              segments={autoSegments} 
              totalDistance={autoPathInfo?.distance || 0} 
              title="Auto Path Segments" 
              className="auto"
            />
            
            {/* Custom path segments table */}
            <SegmentTable 
              segments={customSegments} 
              totalDistance={customPathInfo?.distance || 0} 
              title="Custom Path Segments" 
              className="custom"
            />
            
            {/* Path optimization suggestions */}
            {customSegments.length > 0 && !pathDifference.isCustomBetter && (
              <OptimizationSuggestions 
                customSegments={customSegments} 
                autoSegments={autoSegments} 
                isCustomBetter={pathDifference.isCustomBetter}
              />
            )}
          </div>
        )}

      </div>
    </div>
  );
});

export default ComparisonResult; 